const COUNTER_ID = 23528392;

declare global {
    interface Window {
        ym?: (counterId: number, action: string, ...args: unknown[]) => void;
    }
}

/**
 * Счётчик грузится отложенно – до первого scroll/mousemove/click/touchstart
 * или 5001 мс (`_parts/index/counters.html.twig`), и это сделано ради
 * PageSpeed. Без буфера любая цель, стреляющая по загрузке страницы, а не по
 * действию человека, молча теряется: `window.ym?.()` при отсутствующем `ym`
 * не делает ничего и об этом не сообщает. Именно так годами занижалась
 * `card-pay`.
 *
 * Поэтому: копим до появления `ym` и отправляем пачкой. Отсрочку загрузки не
 * трогаем – платить скоростью за то, что чинится десятком строк, незачем.
 */
const MAX_PENDING = 50;
const POLL_INTERVAL_MS = 300;
const GIVE_UP_AFTER_MS = 30000;

/** Разрез цели. Едет ЧЕТВЁРТЫМ аргументом `ym` — колбэк там пятый. */
export type TGoalParams = Record<string, string>;

type TQueued = { name: string; params?: TGoalParams };

let pending: TQueued[] = [];
let pollTimer: ReturnType<typeof setInterval> | null = null;
let waitedMs = 0;

function stopPolling(): void {
    if (pollTimer !== null) {
        clearInterval(pollTimer);
        pollTimer = null;
    }
}

/**
 * Аргумент разреза ОПУСКАЕТСЯ, когда его нет, а не передаётся `undefined`:
 * так вызов без разреза остаётся ровно тем же, каким был до появления
 * параметров, и поведение живой цели ни от чего нового не зависит.
 */
function send(
    ym: NonNullable<Window['ym']>,
    name: string,
    params?: TGoalParams
): void {
    if (params) {
        ym(COUNTER_ID, 'reachGoal', name, params);
    } else {
        ym(COUNTER_ID, 'reachGoal', name);
    }
}

function flush(): boolean {
    const ym = window.ym;

    if (!ym) {
        return false;
    }

    // Копию берём до отправки: сам ym синхронный, но если он бросит, очередь не
    // должна уйти в вечный повтор одного и того же имени. try/catch на КАЖДОМ
    // имени, а не на всём цикле: иначе бросок на втором из пяти унёс бы с собой
    // три оставшихся.
    const queued = pending;
    pending = [];

    queued.forEach((item) => {
        try {
            send(ym, item.name, item.params);
        } catch {
            // Цель потеряна — но соседние по очереди не виноваты.
        }
    });

    return true;
}

function startPolling(): void {
    if (pollTimer !== null) {
        return;
    }

    waitedMs = 0;

    pollTimer = setInterval(() => {
        waitedMs += POLL_INTERVAL_MS;

        if (flush() || waitedMs >= GIVE_UP_AFTER_MS) {
            // Сдаёмся молча: счётчик мог быть срезан блокировщиком, и это
            // нормальное состояние, а не ошибка, о которой стоит сообщать.
            if (!window.ym) {
                pending = [];
            }

            stopPolling();
        }
    }, POLL_INTERVAL_MS);
}

/**
 * Цель, при необходимости с разрезом.
 *
 * Разрез — параметр СОБЫТИЯ, а не визита. Плечо сплит-теста принадлежит
 * конкретной открытке, а параметры визита копятся на весь визит: посмотрев в
 * одном визите свою открытку и чужую из другого плеча, человек принёс бы оба
 * значения, и следующую `card-pay` нельзя было бы отнести ни к одному. Плюс в
 * тот же сегмент попадала бы пародийная касса — тот же дровер, но плеча у неё
 * нет вовсе.
 */
export function reachGoal(name: string, params?: TGoalParams): void {
    if (!name) {
        return;
    }

    // Мимо очереди — только когда очередь пуста. Иначе цель, вызванная после
    // появления счётчика, обгоняла бы те, что ждут его с прошлой секунды.
    if (window.ym && !pending.length) {
        try {
            send(window.ym, name, params);
        } catch {
            // См. flush(): потеря одной цели не повод ронять вызывающий код.
        }

        return;
    }

    // Потолок нужен на случай, когда счётчика не будет никогда: очередь не
    // должна расти вместе с числом кликов на странице.
    if (pending.length < MAX_PENDING) {
        pending.push({ name, params });
    }

    // Счётчик мог появиться между постановкой в очередь и этой строкой — тогда
    // ждать следующего тика поллера незачем.
    if (flush()) {
        stopPolling();

        return;
    }

    startPolling();
}

/**
 * Цель + переход по ссылке. Метрика не успевает отправить запрос, если сразу
 * после `reachGoal` присвоить `location.href`, поэтому ждём колбэк – но не
 * дольше таймаута, иначе сломанный счётчик заблокирует оплату.
 *
 * Колбэк у `ym` – ПЯТЫЙ аргумент; четвёртым он молча уедет в `params`.
 */
export function reachGoalThen(
    name: string,
    next: () => void,
    params?: TGoalParams,
    timeoutMs = 400
): () => void {
    let done = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    // Отмена нужна не для красоты: между кликом и переходом есть окно в
    // timeoutMs, и в это окно человек успевает закрыть дровер по Escape или
    // клику по фону. Без отмены его всё равно уносило бы на шлюз, который он
    // только что отменил — до буфера навигация была синхронной, и такого окна
    // не существовало вовсе.
    const cancel = () => {
        done = true;

        if (timer !== null) {
            clearTimeout(timer);
            timer = null;
        }
    };

    const proceed = () => {
        if (done) {
            return;
        }

        done = true;
        next();
    };

    if (!window.ym) {
        // Цель уходит в буфер, а навигация случается прямо сейчас — то есть
        // страница уедет вместе с буфером и цель, скорее всего, потеряется.
        // Так и задумано: задержать оплату ради метрики нельзя. Практически
        // редко — к моменту клика сниппет счётчика уже объявил `ym`.
        reachGoal(name, params);
        proceed();

        return cancel;
    }

    // Таймаут регистрируется ДО вызова счётчика, и вызов обёрнут. Иначе бросок
    // из `ym` уносил бы с собой и запасной путь: `next()` не звался бы никогда,
    // а это на двух путях сразу «открытка создана, но перехода на предпросмотр
    // нет» (человек повторит сабмит и заведёт дубль) и «способ оплаты выбран,
    // навигации нет, кнопки уже disabled» — то есть мёртвая касса без единого
    // следа, потому что исключение улетает в чужой колбэк мимо Sentry.
    timer = setTimeout(proceed, timeoutMs);

    try {
        // Разрез уезжает ЧЕТВЁРТЫМ аргументом, колбэк — пятым. Их порядок не
        // выбирается: у `ym` он такой, и параметры, поставленные пятыми, молча
        // заняли бы место колбэка, то есть убили бы переход.
        window.ym(COUNTER_ID, 'reachGoal', name, params ?? {}, proceed);
    } catch {
        proceed();
    }

    return cancel;
}

/**
 * Только для тестов: очередь и таймер живут в модуле.
 *
 * @internal не звать из продового кода — экспорт существует лишь потому, что
 * состояние модульное и иначе тесты не изолировать.
 */
export function __resetGoalQueue(): void {
    pending = [];
    waitedMs = 0;
    stopPolling();
}
